import * as React from 'react';
import { SelectItem } from 'vet-bones/utils/select';

import { LinkCategory } from 'src/containers/ProjectDiagram/constants/categories';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import { DiagramTransaction } from 'src/containers/ProjectDiagram/constants/transactions';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ModelConceptRelationshipLink } from 'src/containers/ProjectDiagram/types/diagram';
import {
  DiagramModelCommitLayout,
  commitDiagramModel,
  commitDiagramWhileSkippingUndoManager,
} from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isLink } from 'src/containers/ProjectDiagram/utils/graphObject';
import { addMappingsToConnectedValidRelationships } from 'src/containers/ProjectDiagram/utils/mapping/addMappingsToConnectedValidRelationships';
import { addModelConceptClass } from 'src/containers/ProjectDiagram/utils/modelConceptClass/addModelConceptClass';
import { getModelConceptClassKey } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { addDomainOrRangeToRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/addDomainOrRangeToRelationship';
import { removeModelConceptRelationshipForClass } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationshipForClass';
import { removePartsFromSelection } from 'src/containers/ProjectDiagram/utils/removePartsFromSelection';

export const useAddClassForRelationship = ({
  direction,
  relationshipIri,
}: {
  direction: RelationshipDirection;
  relationshipIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: SelectItem | null) => {
      if (!item || !diagram || !relationshipIri) {
        return;
      }

      const { value, isNewlyCreated, text } = item;
      commitDiagramModel(
        diagram,
        (model, diagram) => {
          if (isNewlyCreated) {
            addModelConceptClass({ iri: value, label: text })(model);
          }
          const { addedRelationships, reconnectedRelationships } =
            addDomainOrRangeToRelationship({
              direction,
              relationshipIri,
              classIri: value,
            })(model, diagram);
          addMappingsToConnectedValidRelationships([
            ...reconnectedRelationships,
            ...addedRelationships,
          ])(model);
        },
        { layout: DiagramModelCommitLayout.PARTIAL }
      );
    },
    [direction, diagram, relationshipIri]
  );
};

export const useRemoveClassForRelationship = ({
  direction,
  relationshipIri,
}: {
  direction: RelationshipDirection;
  relationshipIri: string | undefined;
}) => {
  const diagram = useDiagram();

  return React.useCallback(
    (item: SelectItem | null) => {
      if (!item || !diagram || !relationshipIri) {
        return;
      }

      const { value } = item;
      const classKey = getModelConceptClassKey(value);
      const keysToRemove = new Set<go.Key>();

      diagram
        .findLinksByExample({
          value: relationshipIri,
          category: LinkCategory.MODEL_CONCEPT_RELATIONSHIP,
          [direction === RelationshipDirection.OUTGOING ? 'from' : 'to']:
            classKey,
        })
        .each((link) => {
          keysToRemove.add(link.data.key);
          link.labelNodes.each((labelNode) => {
            keysToRemove.add(labelNode.data.key);
          });
        });

      const hasOtherDomainsOrRanges = diagram.links.any(
        (link) =>
          isLink<ModelConceptRelationshipLink>(
            link,
            LinkCategory.MODEL_CONCEPT_RELATIONSHIP
          ) &&
          link.data.value === relationshipIri &&
          link.data[
            direction === RelationshipDirection.OUTGOING ? 'from' : 'to'
          ] !== classKey
      );

      // remove links and labels from selection before removing to trigger a `diagramChangedSelection` event
      if (hasOtherDomainsOrRanges) {
        commitDiagramWhileSkippingUndoManager(
          diagram,
          (_, diagram) => removePartsFromSelection(keysToRemove)(diagram),
          DiagramTransaction.SET_DIAGRAM_SELECTION
        );
      }
      commitDiagramModel(
        diagram,
        removeModelConceptRelationshipForClass({
          direction,
          iri: relationshipIri,
          classIri: value,
        })
      );
    },
    [direction, diagram, relationshipIri]
  );
};
